import type { Typed } from 'services/api/fields/core';

export enum QuestionTemplateType {
  DISCHARGE = 'discharge',
  PROGRESS = 'progress',
}
export enum QuestionKind {
  NOTES = 'notes',
  NUMERIC = 'numeric',
  RANGE = 'range',
  SATISFACTION_SCALE = 'satisfaction_scale',
  STATUS = 'status',
  YES_NO = 'yes_no',
}

export type QuestionTypeConfig = {
  chart?: {
    yAxis: string;
    chartName: string;
  };
  optional?: boolean;
  requiredRatings?: string[];
  positive?: string;
};

export interface QuestionType {
  id: string;
  kind: QuestionKind;
  text: string;
  order: number;
  config: QuestionTypeConfig | Record<string, never>;
}

export interface QuestionTemplateOptions {
  questions?: QuestionType[];
  questionCount?: number;
  type?: QuestionTemplateType;
  [key: string]: any;
}

const defaults: QuestionTemplateOptions = {
  id: '',
  clientId: '',
  client: null,
  instructions: '',
  questions: [],
  questionCount: 0,
  type: QuestionTemplateType.PROGRESS,
  trackLos: true,
  episodeClassifications: [],
  planTypeClassifications: [],
  groupTypes: [],
  updatedAt: new Date().toISOString(),
};

let QuestionTemplate: new <T extends QuestionTemplateOptions>(options: T) => Typed<_QuestionTemplate<T>, T>;

class _QuestionTemplate<T extends QuestionTemplateOptions = QuestionTemplateOptions> {
  questionCount!: number;
  type!: QuestionTemplateType;

  constructor(options: T) {
    const opts = { ...defaults, ...options };
    Object.assign(this, opts);
    this.questionCount = (opts.questions?.length ?? 0) > 0 ? opts.questions!.length : (opts.questionCount ?? 0);
    this.type = opts.type ?? QuestionTemplateType.PROGRESS;
  }

  get isProgress() {
    return this.type === QuestionTemplateType.PROGRESS;
  }

  get isDischarge() {
    return this.type === QuestionTemplateType.DISCHARGE;
  }
}

// eslint-disable-next-line prefer-const
QuestionTemplate = _QuestionTemplate as typeof QuestionTemplate;

export default QuestionTemplate;
export { QuestionTemplate };
export type QuestionTemplate<T extends QuestionTemplateOptions = QuestionTemplateOptions> = _QuestionTemplate<T> &
  Omit<T, keyof _QuestionTemplate<T>>;
